import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import duplicateApi from '../../utils/duplicateApi';
import ConfirmationModal from '../ConfirmationModal';
import AlertModal from '../AlertModal';
import DuplicateSummaryCard from './DuplicateSummaryCard';
import DuplicateFilter from './DuplicateFilter';
import DuplicateGroupCard from './DuplicateGroupCard';
import './duplicates.css';

const PAGE_SIZE = 10;

export default function DuplicateEmailsPage({ currentUser, embedded = false }) {
  const navigate = useNavigate();
  const listRef = useRef(null);

  const [summary, setSummary] = useState(null);
  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [folder, setFolder] = useState('');
  const [sort, setSort] = useState('newest');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState(() => new Set());

  const [confirm, setConfirm] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '' });

  const showAlert = (message) => setAlert({ open: true, message });

  const loadSummary = useCallback(async () => {
    try {
      const res = await duplicateApi.summary();
      if (res.success) setSummary(res.data);
    } catch (_) { /* ignore */ }
  }, []);

  const loadGroups = useCallback(async (nextPage = 1) => {
    setLoading(true);
    try {
      const res = await duplicateApi.list({
        page: nextPage,
        limit: PAGE_SIZE,
        q: query,
        folder,
      });
      if (res.success) {
        let items = res.items || [];
        if (sort === 'oldest') {
          items = [...items].sort(
            (a, b) => new Date(a.detectedAt || 0) - new Date(b.detectedAt || 0)
          );
        } else if (sort === 'count') {
          items = [...items].sort((a, b) => (b.memberCount || 0) - (a.memberCount || 0));
        } else if (sort === 'size') {
          items = [...items].sort((a, b) => {
            const sa = (a.members || []).reduce((s, m) => s + (m.size || 0), 0);
            const sb = (b.members || []).reduce((s, m) => s + (m.size || 0), 0);
            return sb - sa;
          });
        } else {
          items = [...items].sort(
            (a, b) => new Date(b.detectedAt || 0) - new Date(a.detectedAt || 0)
          );
        }
        setGroups(items);
        setTotal(res.total || 0);
        setPages(res.pages || 1);
        setPage(res.page || 1);
        setSelectedGroups(new Set());
      }
    } catch (e) {
      showAlert(e.response?.data?.message || e.message || 'Failed to load duplicates');
    } finally {
      setLoading(false);
    }
  }, [query, folder, sort]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await duplicateApi.getSettings();
      if (res.success) setSettings(res.data);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadSummary();
    loadGroups(1);
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadGroups(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await duplicateApi.scan();
      if (!res.success) throw new Error(res.message || 'Scan failed');
      showAlert(
        `Scan complete: ${res.data.groupsFound} group(s) from ${res.data.scanned} message(s).`
      );
      await Promise.all([loadSummary(), loadGroups(1)]);
    } catch (e) {
      showAlert(e.response?.data?.message || e.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const askConfirm = (message, onConfirm) => setConfirm({ message, onConfirm });

  const handleDeleteSelected = (group, members) => {
    askConfirm(
      `Move ${members.length} duplicate email(s) to Trash? You can restore them from Trash.`,
      async () => {
        setConfirm(null);
        try {
          const res = await duplicateApi.remove({ groupId: group.groupId, members });
          showAlert(res.message || `Moved ${res.data?.affectedCount || 0} to Trash`);
          await Promise.all([loadSummary(), loadGroups(page)]);
        } catch (e) {
          showAlert(e.response?.data?.message || e.message);
        }
      }
    );
  };

  const handleCleanup = (group, strategy) => {
    const labels = {
      keep_newest: 'newest',
      keep_oldest: 'oldest',
      keep_starred: 'starred',
      keep_unread: 'unread',
    };
    askConfirm(
      `Keep the ${labels[strategy]} copy and move the rest of this group to Trash?`,
      async () => {
        setConfirm(null);
        try {
          const res = await duplicateApi.merge({ groupId: group.groupId, strategy });
          showAlert(res.message || 'Cleanup done');
          await Promise.all([loadSummary(), loadGroups(page)]);
        } catch (e) {
          showAlert(e.response?.data?.message || e.message);
        }
      }
    );
  };

  const handleBulkCleanup = (strategy) => {
    const labels = {
      keep_newest: 'newest',
      keep_oldest: 'oldest',
      keep_starred: 'starred',
      keep_unread: 'unread',
    };
    askConfirm(
      `For ALL duplicate groups: keep the ${labels[strategy]} copy and move extras to Trash?`,
      async () => {
        setConfirm(null);
        try {
          const res = await duplicateApi.merge({ strategy, all: true });
          showAlert(res.message || 'Bulk cleanup done');
          await Promise.all([loadSummary(), loadGroups(1)]);
        } catch (e) {
          showAlert(e.response?.data?.message || e.message);
        }
      }
    );
  };

  const handleDeleteAll = () => {
    askConfirm(
      'Move ALL extra duplicate copies to Trash (keep newest in each group)? You can restore from Trash.',
      async () => {
        setConfirm(null);
        try {
          const res = await duplicateApi.merge({ strategy: 'keep_newest', all: true });
          showAlert(res.message || 'All duplicates cleaned');
          await Promise.all([loadSummary(), loadGroups(1)]);
        } catch (e) {
          showAlert(e.response?.data?.message || e.message);
        }
      }
    );
  };

  const handleIgnore = async (group) => {
    try {
      await duplicateApi.ignore(group.groupId);
      await Promise.all([loadSummary(), loadGroups(page)]);
    } catch (e) {
      showAlert(e.response?.data?.message || e.message);
    }
  };

  const handleNotDuplicate = async (group) => {
    try {
      await duplicateApi.notDuplicate(group.groupId);
      await Promise.all([loadSummary(), loadGroups(page)]);
    } catch (e) {
      showAlert(e.response?.data?.message || e.message);
    }
  };

  const handleOpenMail = (member) => {
    if (member.source === 'imap' && member.uid) {
      navigate(`/inbox?uid=${member.uid}`);
      return;
    }
    if (member.folder === 'sent') navigate('/sent');
    else if (member.folder === 'trash') navigate('/trash');
    else navigate('/inbox');
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await duplicateApi.updateSettings({
        enabled: settings.enabled,
        sensitivity: settings.sensitivity,
        subjectSimilarity: Number(settings.subjectSimilarity),
        bodySimilarity: Number(settings.bodySimilarity),
        nearDuplicateWindowMinutes: Number(settings.nearDuplicateWindowMinutes),
        autoScanAfterSync: settings.autoScanAfterSync,
        autoIgnoreReadEmails: settings.autoIgnoreReadEmails,
        scanIntervalMinutes: Number(settings.scanIntervalMinutes),
      });
      if (res.success) {
        setSettings(res.data);
        showAlert('Settings saved');
        setSettingsOpen(false);
      }
    } catch (e) {
      showAlert(e.response?.data?.message || e.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const allChecked = groups.length > 0 && selectedGroups.size === groups.length;
  const toggleSelectAll = () => {
    if (allChecked) setSelectedGroups(new Set());
    else setSelectedGroups(new Set(groups.map((g) => g.groupId)));
  };

  const pageNumbers = useMemo(() => {
    const max = Math.min(pages, 7);
    const start = Math.max(1, Math.min(page - 2, pages - max + 1));
    return Array.from({ length: Math.max(0, Math.min(max, pages)) }, (_, i) => start + i)
      .filter((n) => n >= 1 && n <= pages);
  }, [page, pages]);

  const fromIdx = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toIdx = Math.min(page * PAGE_SIZE, total);

  return (
    <div className={`dup-page${embedded ? ' dup-page-embedded' : ''}`}>
      {!embedded && (
      <header className="dup-page-header">
        <div className="dup-page-title-wrap">
          <div className="dup-title-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
              <path d="M12 2l1.8 5.4L19 9.2l-5.2 1.8L12 16l-1.8-5L5 9.2l5.2-1.8z" />
            </svg>
          </div>
          <div>
            <h1>Duplicate Emails</h1>
            <p>
              Find and clean duplicate messages across Inbox and Sent. Cleanup always moves copies to Trash first.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="dup-settings-btn"
          onClick={() => setSettingsOpen((v) => !v)}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96a7.2 7.2 0 0 0-1.62-.94l-.36-2.54A.48.48 0 0 0 14 2h-4a.48.48 0 0 0-.48.41l-.36 2.54c-.58.23-1.12.54-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.65 8.87a.48.48 0 0 0 .12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.13.23.4.32.64.22l2.39-.96c.5.4 1.04.72 1.62.94l.36 2.54c.05.24.25.41.49.41h4c.24 0 .44-.17.49-.41l.36-2.54c.58-.22 1.12-.54 1.62-.94l2.39.96c.24.1.51 0 .64-.22l1.92-3.32a.49.49 0 0 0-.12-.61l-2.03-1.58zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z" />
          </svg>
          Settings
        </button>
      </header>
      )}
      {embedded && (
        <div className="sm-toolbar" style={{ marginBottom: 12 }}>
          <button
            type="button"
            className="dup-settings-btn"
            onClick={() => setSettingsOpen((v) => !v)}
          >
            Detection settings
          </button>
        </div>
      )}

      <DuplicateSummaryCard
        summary={summary}
        scanning={scanning}
        onScan={handleScan}
        onScrollToGroups={() => listRef.current?.scrollIntoView({ behavior: 'smooth' })}
      />

      {settingsOpen && settings && (
        <section className="dup-settings-card">
          <h3>Detection settings</h3>
          <div className="dup-settings-grid">
            <label>
              <input
                type="checkbox"
                checked={Boolean(settings.enabled)}
                onChange={(e) => setSettings((s) => ({ ...s, enabled: e.target.checked }))}
              />
              Enable duplicate detection
            </label>
            <label>
              Sensitivity
              <select
                value={settings.sensitivity}
                onChange={(e) => setSettings((s) => ({ ...s, sensitivity: e.target.value }))}
              >
                <option value="low">Low (exact only)</option>
                <option value="medium">Medium</option>
                <option value="high">High (near duplicates)</option>
              </select>
            </label>
            <label>
              Subject similarity
              <input
                type="number"
                min="0.5"
                max="1"
                step="0.01"
                value={settings.subjectSimilarity}
                onChange={(e) => setSettings((s) => ({ ...s, subjectSimilarity: e.target.value }))}
              />
            </label>
            <label>
              Body similarity
              <input
                type="number"
                min="0.5"
                max="1"
                step="0.01"
                value={settings.bodySimilarity}
                onChange={(e) => setSettings((s) => ({ ...s, bodySimilarity: e.target.value }))}
              />
            </label>
            <label>
              Near-duplicate window (minutes)
              <input
                type="number"
                min="5"
                value={settings.nearDuplicateWindowMinutes}
                onChange={(e) => setSettings((s) => ({ ...s, nearDuplicateWindowMinutes: e.target.value }))}
              />
            </label>
            <label>
              <input
                type="checkbox"
                checked={Boolean(settings.autoIgnoreReadEmails)}
                onChange={(e) => setSettings((s) => ({ ...s, autoIgnoreReadEmails: e.target.checked }))}
              />
              Auto-ignore fully read groups
            </label>
            <label>
              <input
                type="checkbox"
                checked={Boolean(settings.autoScanAfterSync)}
                onChange={(e) => setSettings((s) => ({ ...s, autoScanAfterSync: e.target.checked }))}
              />
              Auto scan after sync
            </label>
            <label>
              Scan interval (minutes)
              <input
                type="number"
                min="15"
                value={settings.scanIntervalMinutes}
                onChange={(e) => setSettings((s) => ({ ...s, scanIntervalMinutes: e.target.value }))}
              />
            </label>
          </div>
          <button type="button" className="dup-primary-btn" onClick={saveSettings} disabled={savingSettings}>
            {savingSettings ? 'Saving…' : 'Save settings'}
          </button>
        </section>
      )}

      <DuplicateFilter
        query={query}
        folder={folder}
        sort={sort}
        onQueryChange={setQuery}
        onFolderChange={setFolder}
        onSortChange={setSort}
        onSubmit={() => loadGroups(1)}
        onBulkCleanup={handleBulkCleanup}
        onDeleteAll={handleDeleteAll}
      />

      {scanning && (
        <div className="dup-progress">
          <div className="dup-progress-bar" />
          <span>Scanning mailbox for duplicates…</span>
        </div>
      )}

      <section className="dup-list-panel" ref={listRef}>
        <div className="dup-list-head">
          <label className="dup-select-all">
            <input type="checkbox" checked={allChecked} onChange={toggleSelectAll} />
            <h2>Duplicate Groups {total > 0 ? `(${total})` : ''}</h2>
          </label>
        </div>

        {loading ? (
          <div className="dup-skeleton-list">
            {[1, 2, 3].map((n) => (
              <div key={n} className="dup-skeleton-card" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="dup-empty">
            <div className="dup-empty-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
              </svg>
            </div>
            <h3>No duplicates found</h3>
            <p>
              {summary?.lastScanAt
                ? 'Your mailbox looks clean. Run another scan after new mail arrives.'
                : 'Click “Scan Now” to check Inbox and Sent for duplicate emails.'}
            </p>
            <button type="button" className="dup-primary-btn" onClick={handleScan} disabled={scanning}>
              {scanning ? 'Scanning…' : 'Scan Now'}
            </button>
          </div>
        ) : (
          <>
            <div className="dup-group-list">
              {groups.map((group) => (
                <DuplicateGroupCard
                  key={group.groupId}
                  group={group}
                  checked={selectedGroups.has(group.groupId)}
                  onCheckChange={(checked) => {
                    setSelectedGroups((prev) => {
                      const next = new Set(prev);
                      if (checked) next.add(group.groupId);
                      else next.delete(group.groupId);
                      return next;
                    });
                  }}
                  onDeleteSelected={handleDeleteSelected}
                  onCleanup={handleCleanup}
                  onIgnore={handleIgnore}
                  onNotDuplicate={handleNotDuplicate}
                  onOpenMail={handleOpenMail}
                />
              ))}
            </div>

            <footer className="dup-pagination-bar">
              <span className="dup-page-info">
                Showing {fromIdx} to {toIdx} of {total} groups
              </span>
              <div className="dup-page-controls">
                <button
                  type="button"
                  className="dup-page-btn"
                  disabled={page <= 1}
                  onClick={() => loadGroups(page - 1)}
                  aria-label="Previous page"
                >
                  ‹
                </button>
                {pageNumbers.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`dup-page-btn ${n === page ? 'active' : ''}`}
                    onClick={() => loadGroups(n)}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  className="dup-page-btn"
                  disabled={page >= pages}
                  onClick={() => loadGroups(page + 1)}
                  aria-label="Next page"
                >
                  ›
                </button>
                <span className="dup-page-size">{PAGE_SIZE} / page</span>
              </div>
            </footer>
          </>
        )}
      </section>

      <ConfirmationModal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm?.onConfirm?.()}
        message={confirm?.message || ''}
        confirmText="Move to Trash"
        cancelText="Cancel"
      />

      <AlertModal
        isOpen={alert.open}
        onClose={() => setAlert({ open: false, message: '' })}
        message={alert.message}
      />

      <span className="dup-sr-only">{currentUser?.email}</span>
    </div>
  );
}
